import globalStompService from './GlobalStompService';

let scopeSeed = 0;

function isFn(value) {
	return typeof value === 'function';
}

function normalizeScopeOptions(input) {
	if (typeof input === 'string') {
		return {
			name: input
		};
	}
	return input || {};
}

function buildScopeId(options) {
	if (options.scopeId) {
		return options.scopeId;
	}

	const scopeName = options.name || 'default';
	if (options.vm && typeof options.vm._uid !== 'undefined') {
		return 'vm:' + options.vm._uid + ':' + scopeName;
	}

	scopeSeed += 1;
	return 'scope:' + scopeName + ':' + scopeSeed;
}

function attachScopeToVm(vm, scopeId) {
	if (!vm || !isFn(vm.$once)) {
		return;
	}

	if (!vm.__globalStompScopeIds) {
		vm.__globalStompScopeIds = Object.create(null);
	}

	if (vm.__globalStompScopeIds[scopeId]) {
		return;
	}

	vm.__globalStompScopeIds[scopeId] = true;
	vm.$once('hook:beforeDestroy', function cleanupScopeOnDestroy() {
		globalStompService.destroyScope(scopeId);
		if (this.__globalStompScopeIds) {
			delete this.__globalStompScopeIds[scopeId];
		}
	});
}

function buildScopeFacade(scopeId, options) {
	const scopeMetadata = {
		name: options.name || 'default'
	};

	if (options.vm && options.vm.$options && options.vm.$options.name) {
		scopeMetadata.componentName = options.vm.$options.name;
	}

	if (options.vm && typeof options.vm._uid !== 'undefined') {
		scopeMetadata.vmUid = options.vm._uid;
	}

	if (options.vm && options.vm.$route && options.vm.$route.path) {
		scopeMetadata.routePath = options.vm.$route.path;
	}

	globalStompService.ensureScope(scopeId, scopeMetadata);

	return {
		id: scopeId,
		connect() {
			globalStompService.start({
				reason: 'scope-connect:' + scopeId
			});
			return this;
		},
		disconnect() {
			globalStompService.destroyScope(scopeId);
			return this;
		},
		destroy() {
			globalStompService.destroyScope(scopeId);
			return this;
		},
		subscribe(destination, handler, subscribeOptions) {
			return globalStompService.subscribe(scopeId, destination, handler, subscribeOptions);
		},
		unsubscribe(keyOrDestination) {
			globalStompService.unsubscribe(scopeId, keyOrDestination);
			return this;
		},
		send(destination, body, headers) {
			globalStompService.send(destination, body, headers);
			return this;
		},
		isConnected() {
			return globalStompService.isConnected();
		},
		isActive() {
			return globalStompService.isActive();
		},
		getState() {
			return globalStompService.getState();
		}
	};
}

function normalizeLegacyConfig(options) {
	if (!options) {
		return null;
	}

	return {
		url: options.url,
		baseUrl: options.baseUrl,
		path: options.path,
		connectHeaders: options.connectHeaders,
		headersProvider: options.headersProvider,
		stompDebug: options.stompDebug,
		heartbeatOutgoingMs: options.heartbeatIntervalMs,
		heartbeatIncomingMs: options.heartbeatIntervalMs,
		reconnect: options.reconnect
	};
}

export function configureGlobalStomp(config) {
	globalStompService.configure(config || {});
	return globalStompService;
}

export function connectGlobalStomp(metadata) {
	globalStompService.start(metadata || {});
	return globalStompService;
}

export function disconnectGlobalStomp(options) {
	globalStompService.stop(options || {});
	return globalStompService;
}

export function createGlobalStompScope(input) {
	const options = normalizeScopeOptions(input);
	const scopeId = buildScopeId(options);

	if (options.replace !== false) {
		globalStompService.destroyScope(scopeId);
	}

	if (options.vm && options.autoDestroy !== false) {
		attachScopeToVm(options.vm, scopeId);
	}

	return buildScopeFacade(scopeId, options);
}

export function createSmartStompSocket(options) {
	const legacyConfig = normalizeLegacyConfig(options);
	if (legacyConfig) {
		configureGlobalStomp(legacyConfig);
	}

	return createGlobalStompScope({
		vm: options && options.vm,
		name: (options && options.scopeId) || 'legacy',
		replace: options && Object.prototype.hasOwnProperty.call(options, 'replace') ? options.replace : true
	});
}

export { globalStompService };
export default globalStompService;